import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import {
  useUpdateNoteChangeMutation,
  type NoteChangeDetail,
  type NoteChangePayload,
} from "../../../app/services/notes";
import NoteEditor, { type NoteSavePayload } from "./NoteEditor";
import SubmitChangeModal from "./SubmitChangeModal";

/**
 * 起票者による変更提案の内容修正モーダル（PR 詳細の「内容を修正」用）。
 *
 * - レビュー待ち: 「修正を保存」（レビュー待ちのまま内容を更新。
 *   既存の承認は無効になり数え直しになる）
 * - 下書き（?change= 直リンク等のフォールバック）: 「下書き保存」または
 *   「プルリクエスト」（説明を添えて送信）。通常の下書き編集はツリーの
 *   下書き行 → 通常ページ表示（?draft=）の埋め込みエディタで行う
 *
 * 変更提案の payload 形は target_type で固定のため、エディタの表示形式
 * 切替は出さない（note = title/content のみ / card = カードメタ付き）。
 */

/** エディタ出力を target_type に応じた payload 形へ落とす */
export const toChangePayload = (
  targetType: NoteChangeDetail["target_type"],
  payload: NoteSavePayload,
): NoteChangePayload =>
  targetType === "card"
    ? {
        title: payload.title,
        content: payload.content,
        category: payload.category,
        expires_at: payload.expires_at,
        is_required: payload.is_required,
        is_published: payload.is_published,
      }
    : { title: payload.title, content: payload.content };

export interface ChangeEditModalProps {
  change: NoteChangeDetail;
  onClose: () => void;
}

const ChangeEditModal: React.FC<ChangeEditModalProps> = ({
  change,
  onClose,
}) => {
  const [updateChange, { isLoading: saving }] = useUpdateNoteChangeMutation();
  const [pendingPayload, setPendingPayload] =
    useState<NoteChangePayload | null>(null);

  const isDraft = change.status === "draft";

  const save = useCallback(
    async (payload: NoteChangePayload, submit: boolean, description?: string) => {
      try {
        await updateChange({
          change_id: change.id,
          payload,
          ...(description !== undefined ? { description } : {}),
          ...(submit ? { submit: true } : {}),
        }).unwrap();
        toast.success(
          submit
            ? "プルリクエストを送信しました"
            : isDraft
              ? "下書きを保存しました"
              : "修正を保存しました",
        );
        setPendingPayload(null);
        onClose();
      } catch {
        toast.error("保存に失敗しました");
      }
    },
    [change.id, isDraft, onClose, updateChange],
  );

  return (
    <>
      <Modal
        title={isDraft ? "下書きを編集" : "提案内容を修正"}
        titleSize="medium"
        description={
          isDraft
            ? undefined
            : "レビュー待ちの内容を修正します。修正すると既存の承認は無効になり、数え直しになります。"
        }
        size="wide"
        onClose={onClose}
      >
        <NoteEditor
          initialTitle={change.payload.title}
          initialContent={change.payload.content}
          initialMeta={{ ...change.payload, display_style: change.target_type }}
          saving={saving}
          saveLabel={isDraft ? "プルリクエスト" : "修正を保存"}
          onSave={(raw) => {
            const payload = toChangePayload(change.target_type, raw);
            if (isDraft) {
              setPendingPayload(payload);
            } else {
              void save(payload, false);
            }
          }}
          draftSaveLabel={isDraft ? "下書き保存" : undefined}
          onSaveDraft={
            isDraft
              ? (raw) =>
                  void save(toChangePayload(change.target_type, raw), false)
              : undefined
          }
          onCancel={onClose}
        />
      </Modal>

      {pendingPayload && (
        <SubmitChangeModal
          title={pendingPayload.title}
          loading={saving}
          initialDescription={change.description}
          onSubmit={(description) =>
            void save(pendingPayload, true, description)
          }
          onClose={() => setPendingPayload(null)}
        />
      )}
    </>
  );
};

export default ChangeEditModal;
